import { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Search, ExternalLink, CheckCircle2, AlertTriangle, RefreshCw,
  Upload, Copy, FileCheck2, Zap,
} from "lucide-react";
import { adminApi, API, formatApiError } from "./shared";
import { InvescoGuideAnimation } from "./InvescoGuideAnimation";
import { LngGuideAnimation } from "./LngGuideAnimation";

const HELPER_ANIMATIONS = {
  invesco: InvescoGuideAnimation,
  lng:     LngGuideAnimation,
};

const ISIN_RE = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;

// Admin defaults — overridable via props for the user-facing variant.
const DEFAULT_UPLOAD_ENDPOINTS = {
  WisdomTree: `${API}/admin/wisdomtree-import`,
  Invesco:    `${API}/admin/invesco-import`,
  'L&G':      `${API}/admin/lng-import`,
};
const DEFAULT_LOOKUP_PATH = `${API}/admin/holdings-url/lookup`;
const DEFAULT_AUTO_IMPORT_PATH = `${API}/admin/holdings-url/auto-import`;

/**
 * "Trova URL Holdings" dialog — admin AND user-facing.
 *
 * Single ISIN entry, branches on `mode` from the lookup endpoint:
 *
 *   • mode = "auto"   — backend fetches + parses the holdings file
 *     end-to-end. The user clicks ONE button. Used for iShares, VanEck,
 *     Amundi, ARK, Global X, Xtrackers.
 *   • mode = "guided" — backend identifies the issuer; the user
 *     follows the instructions to download the file then drops it
 *     into the dropzone. Used for WisdomTree, Invesco, L&G.
 *
 * Props:
 *   open, onClose, onCompleted              — dialog lifecycle
 *   apiClient   = adminApi                  — axios instance to use
 *   uploadEndpoints = DEFAULT_UPLOAD_ENDPOINTS — issuer → POST URL (guided)
 *   lookupPath  = DEFAULT_LOOKUP_PATH       — GET <path>/{isin}
 *   autoImportPath = DEFAULT_AUTO_IMPORT_PATH — POST <path>/{isin} (auto)
 *   initialIsin = ""                        — auto-search this ISIN on open
 */
export function FindHoldingsUrlDialog({
  open, onClose, onCompleted,
  apiClient = adminApi,
  uploadEndpoints = DEFAULT_UPLOAD_ENDPOINTS,
  lookupPath = DEFAULT_LOOKUP_PATH,
  autoImportPath = DEFAULT_AUTO_IMPORT_PATH,
  initialIsin = "",
}) {
  const [isin, setIsin] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setResult(null);
    setIsin("");
    setImportResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const runLookup = useCallback(async (rawIsin) => {
    const cleaned = (rawIsin || '').trim().toUpperCase();
    if (!ISIN_RE.test(cleaned)) {
      toast.error("Formato ISIN non valido (12 caratteri alfanumerici).");
      return;
    }
    setLoading(true);
    setResult(null);
    setImportResult(null);
    try {
      const { data } = await apiClient.get(
        `${lookupPath}/${encodeURIComponent(cleaned)}`,
      );
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err, "Lookup fallita"));
    } finally {
      setLoading(false);
    }
  }, [apiClient, lookupPath]);

  const onSubmit = useCallback((e) => {
    e?.preventDefault?.();
    runLookup(isin);
  }, [isin, runLookup]);

  // Pre-fill + auto-search when opened with a specific ISIN.
  useEffect(() => {
    if (open && initialIsin) {
      const cleaned = initialIsin.trim().toUpperCase();
      setIsin(cleaned);
      if (ISIN_RE.test(cleaned)) {
        runLookup(cleaned);
      }
    }
  }, [open, initialIsin, runLookup]);

  const handleCopyLink = useCallback(() => {
    if (!result?.link) return;
    navigator.clipboard.writeText(result.link).then(
      () => toast.success("Link copiato"),
      () => toast.error("Impossibile copiare — clicca con il tasto destro sul link"),
    );
  }, [result]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const endpoint = uploadEndpoints[result?.issuer];
    if (!endpoint) {
      toast.error(`Nessun handler di import per ${result?.issuer}`);
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Iter 86d — pass the ISIN the user already typed in step 1, so
      // the backend doesn't need to re-extract it from the filename.
      // Some issuer downloads (L&G in particular) produce filenames
      // that don't carry the ISIN at all, so requiring the user to
      // rename was a friction we can avoid.
      if (result?.isin) formData.append("isin", result.isin);
      const { data } = await apiClient.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(data.message || "Import completato");
      onCompleted?.();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Import fallito";
      setImportResult({ error: detail });
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  }, [result, onCompleted, apiClient, uploadEndpoints]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const onPick = useCallback((e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  // Auto-import — one-click flow for providers whose holdings file the
  // server can fetch + parse end-to-end (iShares, VanEck, Amundi, ARK,
  // Global X, Xtrackers).
  const handleAutoImport = useCallback(async () => {
    if (!result?.isin) return;
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await apiClient.post(
        `${autoImportPath}/${encodeURIComponent(result.isin)}`,
      );
      setImportResult(data);
      toast.success(data.message || "Import completato");
      onCompleted?.();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Import fallito";
      setImportResult({ error: detail });
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  }, [result, autoImportPath, apiClient, onCompleted]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading && !importing) onClose(); }}>
      <DialogContent
        data-testid="find-holdings-url-dialog"
        className="bg-zinc-900 border-zinc-700 max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="text-white text-lg">
            Trova URL Holdings

          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm leading-relaxed pt-2">
            Inserisci l'ISIN di un ETF: il sistema interroga justETF per
            identificarne l'emittente e — se è tra quelli supportati — ti
            offre il link diretto alla pagina di download degli holdings.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex gap-2 my-2">
          <input
            data-testid="find-holdings-url-isin-input"
            value={isin}
            onChange={(e) => setIsin(e.target.value.toUpperCase())}
            placeholder="Es. IE00BLPK3577"
            maxLength={12}
            autoFocus
            disabled={loading}
            className="flex-1 bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-zinc-100 text-sm font-mono focus:outline-none focus:border-blue-500"
          />
          <Button
            data-testid="find-holdings-url-search"
            type="submit"
            disabled={loading || !ISIN_RE.test(isin.trim().toUpperCase())}
            className="bg-blue-600 hover:bg-blue-500"
          >
            {loading
              ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />Cerco…</>
              : <><Search className="w-4 h-4 mr-1.5" />Cerca</>}
          </Button>
        </form>

        {result?.supported && (
          <div
            data-testid="find-holdings-url-supported"
            className="rounded-lg border border-emerald-700/40 bg-emerald-600/10 p-4"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-emerald-300 font-semibold">
                  Emittente supportato: {result.issuer}
                  {result.mode === 'auto' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-emerald-500/20 text-emerald-200 border border-emerald-500/40">
                      Auto-import
                    </span>
                  )}
                  {result.mode === 'guided' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-amber-500/20 text-amber-200 border border-amber-500/40">
                      Workflow guidato
                    </span>
                  )}
                </p>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 mt-2 text-xs">
                  <dt className="text-zinc-500">ETF</dt>
                  <dd className="text-zinc-200">{result.etf_name}</dd>
                  <dt className="text-zinc-500">ISIN</dt>
                  <dd className="text-zinc-200 font-mono">{result.isin}</dd>
                </dl>

                {/* AUTO mode — single-click import. */}
                {result.mode === 'auto' && !importResult?.status && (
                  <div className="mt-3">
                    <p className="text-zinc-400 text-xs leading-relaxed mb-2">
                      Il sistema scarica e importa gli holdings automaticamente:
                      nessun download manuale richiesto.
                    </p>
                    <Button
                      data-testid="find-holdings-url-auto-import"
                      onClick={handleAutoImport}
                      disabled={importing}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    >
                      {importing ? (
                        <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                          Importazione in corso…
                        </>
                      ) : (
                        <><Zap className="w-4 h-4 mr-1.5" />
                          Importa automaticamente
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* GUIDED mode — link + instructions. */}
                {result.mode === 'guided' && (
                  <>
                    <a
                      data-testid="find-holdings-url-link"
                      href={result.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded border border-emerald-600/40 bg-emerald-600/10 text-emerald-300 hover:bg-emerald-600/20 hover:text-emerald-200 text-xs font-medium transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {result.kind === "holdings_csv"
                        ? "Scarica gli holdings"
                        : "Apri la pagina dell'ETF"}
                    </a>
                    <Button
                      data-testid="find-holdings-url-copy"
                      size="sm"
                      variant="ghost"
                      onClick={handleCopyLink}
                      className="ml-2 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 h-7 px-2 text-xs"
                      title="Copia il link negli appunti"
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" />Copia
                    </Button>
                    {result.instructions && (
                      <div className="mt-2 flex gap-3 items-start">
                        <p className="text-zinc-400 text-xs leading-relaxed whitespace-pre-line flex-1 min-w-0">
                          {result.instructions}
                        </p>
                        {(() => {
                          const Anim = HELPER_ANIMATIONS[result.helper_animation];
                          return Anim ? <Anim /> : null;
                        })()}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Step 2 (guided): drop zone for the downloaded CSV. */}
            {result.mode === 'guided'
              && uploadEndpoints[result.issuer]
              && !importResult?.status && (
              <div
                data-testid="find-holdings-url-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => !importing && fileRef.current?.click()}
                className={`mt-4 border-2 border-dashed rounded-md p-5 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-emerald-500 bg-emerald-600/10'
                    : 'border-zinc-700 hover:border-zinc-600 bg-zinc-950/40'
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={onPick}
                  className="hidden"
                  disabled={importing}
                  data-testid="find-holdings-url-file-input"
                />
                {importing ? (
                  <div className="flex items-center justify-center gap-2 text-zinc-300 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Importazione in corso…
                  </div>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-1.5" />
                    <p className="text-zinc-300 text-sm">
                      <span className="font-medium">Step 2.</span> Una volta
                      scaricato il CSV, trascinalo qui (o clicca per
                      selezionarlo) per importarlo.
                    </p>
                    <p className="text-zinc-500 text-xs mt-1">
                      L'ETF target viene identificato automaticamente dal file.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Step 2b: import success */}
            {importResult?.status === 'imported' && (
              <div
                data-testid="find-holdings-url-import-success"
                className="mt-4 rounded-md border border-emerald-700/40 bg-emerald-600/15 p-3"
              >
                <div className="flex items-start gap-2">
                  <FileCheck2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="text-emerald-300 font-semibold">
                      Holdings importati
                    </p>
                    <p className="text-zinc-300 mt-0.5">{importResult.message}</p>
                    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 mt-2 text-xs">
                      <dt className="text-zinc-500">ETF</dt>
                      <dd className="text-zinc-200 font-mono">
                        {importResult.etf_ticker} · {importResult.etf_name}
                      </dd>
                      <dt className="text-zinc-500">Holdings</dt>
                      <dd className="text-zinc-200 font-mono">{importResult.holdings_count}</dd>
                      <dt className="text-zinc-500">ETF nel DB?</dt>
                      <dd className={importResult.matched_existing_etf ? 'text-emerald-400' : 'text-amber-400'}>
                        {importResult.matched_existing_etf ? 'Sì, aggiornato' : 'No, creato nuovo'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2c: import error */}
            {importResult?.error && (
              <div
                data-testid="find-holdings-url-import-error"
                className="mt-4 rounded-md border border-red-700/40 bg-red-600/15 p-3"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="text-red-300 font-semibold">
                      Importazione fallita
                    </p>
                    <p className="text-zinc-300 mt-0.5">{importResult.error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {result && !result.supported && (
          <div
            data-testid="find-holdings-url-unsupported"
            className="rounded-lg border border-amber-700/40 bg-amber-600/10 p-4"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-amber-300 font-semibold">
                  {result.etf_name
                    ? `Emittente "${result.issuer || '?'}" non ancora supportato`
                    : "ETF non trovato su justETF"}
                </p>
                <p className="text-zinc-300 mt-1 leading-relaxed">
                  {result.message}
                </p>
                <div className="mt-3">
                  <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1.5">
                    Emittenti supportati
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(result.supported_providers || []).map((p) => (
                      <span
                        key={p}
                        className="px-2 py-0.5 rounded-full border border-zinc-600 bg-zinc-800/60 text-zinc-200 text-xs"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="flex justify-end mt-2">
            <Button
              data-testid="find-holdings-url-reset"
              size="sm"
              variant="outline"
              onClick={reset}
              className="border-zinc-700 text-zinc-300"
            >
              Cerca un altro ISIN
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
